/**
 * @file Hook wrapping the `retrySignifyFetchJob` callable. Consumer: `useSignifyFetchJobProgress`,
 * whose one footer button sends the operator's picks and resets every failed part in the same call.
 * `retrying` is what keeps a second press from queueing the same resets twice; `execute` rethrows
 * the callable's own error so the caller can show its words — and read its `HttpsError.code` — and
 * leave the picks on screen to correct.
 */

import { useCallback } from 'react';
import type { RetrySignifyFetchJobRequest, RetrySignifyFetchJobResponse } from '#types';
import { retrySignifyFetchJob } from '../services/signifyApiService';
import { useSignifyApi } from './useSignifyApi';

export const useRetrySignifyFetchJob = () => {
  const api = useSignifyApi<RetrySignifyFetchJobResponse>();

  const retryFetchJob = useCallback(
    async (request: RetrySignifyFetchJobRequest): Promise<RetrySignifyFetchJobResponse> =>
      api.execute(() => retrySignifyFetchJob(request)),
    [api],
  );

  return {
    retrying: api.loading,
    error: api.error,
    retryFetchJob,
  };
};
